/* eslint-disable complexity */
import classNames from 'classnames';
import {useEffect, useRef} from 'react';
import queryString from 'query-string';
import {EDITOR_MODE, RepositoryScope, RepositorySourceType} from '@/Constant';
import Like from '@/domains/document/DocHome/common/like/Like';
import KnowledgeCoin from '@/domains/document/DocHome/common/KnowledgeCoin';
import LikeAndCoin from '@/domains/document/DocHome/common/LikeAndCoin';
import * as store from '@/store';
import kuPiper from '@/middleware/kuPiper';
import NoticeReadStatus from '@/domains/space/Department/DepartmentNotice/components/NoticeReadStatus';
import TodoConflictPopper from '@/domains/space/Repository/components/TodoConflictPopper';
import {getIsBaikeRepo} from '@/pages/baike/utils';
import styles from './index.module.scss';
import {removeSearchParamNoRefresh} from '@/utils/urlUtils';
export const rootId = 'ku_editor_children_container';

import {withAfterDomReady} from '@/components/AfterDomReady';
import {importAsyncComponent} from '@/utils/loadable';
import {VIEW_BLOCK} from '@/hooks/queryViewController/config';
import {isShowCurrentView} from '@/hooks/queryViewController';

const BottomComment = withAfterDomReady(
    importAsyncComponent(() => import('@/domains/document/components/BottomComment'))
);

export interface IProps {
    repository: Repository;
    docGuid: string;
    creatorGuid?: string;
    creatorName?: string;
    mode: EDITOR_MODE;
    showEditorChildren?: boolean;
    isDocCiteShow?: boolean;
    isCardView?: boolean;
}

const EditorChildren: React.FC<IProps> = ({
    mode,
    docGuid,
    repository,
    creatorGuid,
    creatorName,
    showEditorChildren = false,
    isCardView,
}) => {
    const curRepository = store.getCurRepository();
    const isBaikeRepo = getIsBaikeRepo(curRepository?.repositoryGuid || '');
    const isMeetingMode = mode === EDITOR_MODE.meeting;
    const isNormalMode = mode === EDITOR_MODE.normal;
    const isPreview = mode === EDITOR_MODE.preview;
    const showComment = isPreview || isMeetingMode || isNormalMode;
    const ref = useRef<HTMLDivElement>(null);


    useEffect(() => {
        const params = queryString.parse(location.search);
        const isFromPersonalPageDynamic = !!params.scrollToBottom;
        if (isPreview && isFromPersonalPageDynamic && showEditorChildren) {
            ref.current && ref.current.scrollIntoView();
            removeSearchParamNoRefresh('scrollToBottom');
        }
    }, [isPreview, showEditorChildren]);

    if (!showEditorChildren) {
        return null;
    }

    const showLikeAndCoinArea = isShowCurrentView(VIEW_BLOCK.activityBar);
    const showBottomCommentArea = isShowCurrentView(VIEW_BLOCK.bottomComment);

    /**
     * 注意
     * 因布局 & 百科插槽 导致这里的样式经常会出问题
     * 目前（4.11）暂时优先修复问题 后续会考虑优化这部分代码 避免相互影响
     * 如有需求要改这里 需要测试以下几个场景
     * - 普通文档 知识币小流量用户的展示
     * - 普通文档 知识币非小流量用户的展示
     * - 百科文档 知识币小流量用户的展示
     * - 百科文档 知识币非小流量用户的展示
    */
    return (
        <div id={rootId} className={classNames(styles.childrenWrap, isCardView && styles.cardView)} ref={ref}>
            <TodoConflictPopper />
            {
                curRepository?.sourceType === RepositorySourceType.notice ? (
                    <NoticeReadStatus />
                ) : null
            }
            {
                // 开放replace插槽
                kuPiper.openNormalReplaceSlot(
                    {
                        scope: kuPiper.slotScopeMap.DocumentEditor,
                        position: kuPiper.slotPositionMap.DocumentEditor.LikeAndCoinBtn,
                        // 这个插槽涉及到百科的地步点赞 需要注意整体布局
                        DefaultComponent: showLikeAndCoinArea && (isPreview || isNormalMode) && <LikeAndCoin
                            docGuid={docGuid}
                            repository={repository}
                            creatorName={creatorName}
                            creatorGuid={creatorGuid}
                        />,
                    }
                )
            }
            {
                // 开放add插槽
                kuPiper.openNormalAddSlot(
                    {
                        scope: kuPiper.slotScopeMap.DocumentEditor,
                        position: kuPiper.slotPositionMap.DocumentEditor.Bottom,
                    }
                )
            }
            {
                // 开放replace插槽
                kuPiper.openNormalReplaceSlot(
                    {
                        scope: kuPiper.slotScopeMap.DocumentEditor,
                        position: kuPiper.slotPositionMap.DocumentEditor.Comment,
                        // 这个插槽涉及到百科的地步点赞 需要注意整体布局
                        DefaultComponent: (showComment && showBottomCommentArea && store.getSDKShowDocComment()) && (
                            <BottomComment
                                docGuid={docGuid}
                                repository={repository}
                                readonly={isMeetingMode}
                            />
                        ),
                    }
                )
            }
        </div>
    );
};

export default store.connect([store.KEYS.SHOW_EDITOR_CHILDREN])(EditorChildren);
